import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Download, Share2, Trophy, Award, Star, CheckCircle,
  Loader2, Copy, Twitter, Facebook, Linkedin, Link2,
  Lock, Target, Gift, Crown, Gem, Medal
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LEVEL_ICONS = {
  medal: Medal,
  award: Award,
  trophy: Trophy,
  crown: Crown,
  gem: Gem
};

export default function ShareableBadges() {
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [badgeProgress, setBadgeProgress] = useState([]);
  const [stats, setStats] = useState(null);
  const [constancias, setConstancias] = useState([]);
  const [recognitions, setRecognitions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [userLevel, setUserLevel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('badges');
  const canvasRef = useRef(null);
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [badgesRes, progressRes, constanciasRes, recognitionsRes, userProgressRes] = await Promise.all([
        axios.get(`${API_URL}/api/badges/my-badges`, { headers }).catch(() => ({ data: { badges: [], total_points: 0, level: null } })),
        axios.get(`${API_URL}/api/badges/progress`, { headers }).catch(() => ({ data: { progress: [], stats: {} } })),
        axios.get(`${API_URL}/api/recognition/my-constancias`, { headers }).catch(() => ({ data: { constancias: [] } })),
        axios.get(`${API_URL}/api/recognition/my-recognitions`, { headers }).catch(() => ({ data: { recognitions: [] } })),
        axios.get(`${API_URL}/api/recognition/my-progress`, { headers }).catch(() => ({ data: {} }))
      ]);
      
      setEarnedBadges(badgesRes.data.badges || []);
      setUserLevel(badgesRes.data.level || null);
      setBadgeProgress(progressRes.data.progress || []);
      setStats(progressRes.data.stats || {});
      setConstancias(constanciasRes.data.constancias || []);
      setRecognitions(recognitionsRes.data.recognitions || []);
      setProgress(userProgressRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateBadgeImage = (badge, type) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    
    const ctx = canvas.getContext('2d');
    canvas.width = 1200;
    canvas.height = 630;
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    if (type === 'recognition') {
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(0.5, '#2d1f3d');
      gradient.addColorStop(1, '#1a1a2e');
    } else if (type === 'badge') {
      gradient.addColorStop(0, '#1e3a5f');
      gradient.addColorStop(0.5, '#2a4a7f');
      gradient.addColorStop(1, '#1e3a5f');
    } else {
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(1, '#16213e');
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Gold border
    const borderColor = badge.color || '#d4af37';
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = 4;
    ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);
    
    // Inner border
    ctx.strokeStyle = `${borderColor}50`;
    ctx.lineWidth = 1;
    ctx.strokeRect(40, 40, canvas.width - 80, canvas.height - 80);
    
    // UPIM text
    ctx.fillStyle = borderColor;
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('UNIÓN DE PROFESIONALES INMOBILIARIOS DE MÉXICO', canvas.width / 2, 100);
    
    // Badge type
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 48px Arial';
    const typeText = type === 'recognition' ? 'RECONOCIMIENTO' : type === 'badge' ? 'INSIGNIA' : 'CONSTANCIA';
    ctx.fillText(typeText, canvas.width / 2, 180);
    
    // Title
    ctx.fillStyle = borderColor;
    ctx.font = 'bold 36px Arial';
    const title = type === 'recognition' ? badge.program_name : type === 'badge' ? badge.name : badge.content_title;
    const maxWidth = canvas.width - 200;
    wrapText(ctx, title, canvas.width / 2, 280, maxWidth, 44);
    
    // User name / points
    ctx.fillStyle = '#ffffff';
    ctx.font = '28px Arial';
    if (type === 'badge') {
      ctx.fillText(`${badge.points} puntos`, canvas.width / 2, 400);
    } else {
      ctx.fillText(progress?.affiliation_number || 'Socio UPIM', canvas.width / 2, 400);
    }
    
    // Achievement info
    if (type === 'recognition') {
      ctx.fillStyle = borderColor;
      ctx.font = '24px Arial';
      ctx.fillText(`${badge.total_constancias} constancias completadas`, canvas.width / 2, 480);
    }
    
    // Verification code or category
    ctx.fillStyle = '#888888';
    ctx.font = '18px Arial';
    if (badge.verification_code) {
      ctx.fillText(`Verificar: ${badge.verification_code}`, canvas.width / 2, 560);
    } else if (badge.category) {
      ctx.fillText(`Categoría: ${badge.category}`, canvas.width / 2, 560);
    }
    
    // Decorative stars
    ctx.fillStyle = borderColor;
    ctx.font = '40px Arial';
    ctx.fillText('★', 100, 315);
    ctx.fillText('★', canvas.width - 100, 315);
    
    return canvas.toDataURL('image/png');
  };

  const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
    const words = text.split(' ');
    let line = '';
    let currentY = y;

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && i > 0) {
        ctx.fillText(line.trim(), x, currentY);
        line = words[i] + ' ';
        currentY += lineHeight;
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line.trim(), x, currentY);
  };

  const handleDownloadBadge = (badge, type) => {
    const dataUrl = generateBadgeImage(badge, type);
    if (!dataUrl) return;
    
    const link = document.createElement('a');
    link.download = `UPIM_${type}_${badge.verification_code || badge.slug || badge.id}.png`;
    link.href = dataUrl;
    link.click();
  };

  const handleCopyLink = (badge, type) => {
    let url;
    if (type === 'recognition') {
      url = `${window.location.origin}/verificar-reconocimiento/${badge.verification_code}`;
    } else if (type === 'constancia') {
      url = `${window.location.origin}/verificar-constancia/${badge.verification_code}`;
    } else {
      url = `${window.location.origin}/mis-insignias`;
    }
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareTwitter = (badge, type) => {
    let text, url;
    if (type === 'recognition') {
      text = `🏆 ¡Obtuve el reconocimiento "${badge.program_name}" en UPIM Academia! #UPIM #InmobiliariosVerificados`;
      url = `${window.location.origin}/verificar-reconocimiento/${badge.verification_code}`;
    } else if (type === 'badge') {
      text = `🎖️ ¡Obtuve la insignia "${badge.name}" en UPIM Academia! +${badge.points} puntos #UPIM #Logros`;
      url = `${window.location.origin}/mis-insignias`;
    } else {
      text = `📜 ¡Completé "${badge.content_title}" en UPIM Academia! #UPIM #EducaciónInmobiliaria`;
      url = `${window.location.origin}/verificar-constancia/${badge.verification_code}`;
    }
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const handleShareLinkedIn = (badge, type) => {
    let url;
    if (type === 'recognition') {
      url = `${window.location.origin}/verificar-reconocimiento/${badge.verification_code}`;
    } else if (type === 'constancia') {
      url = `${window.location.origin}/verificar-constancia/${badge.verification_code}`;
    } else {
      url = `${window.location.origin}/mis-insignias`;
    }
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const handleShareFacebook = (badge, type) => {
    let url;
    if (type === 'recognition') {
      url = `${window.location.origin}/verificar-reconocimiento/${badge.verification_code}`;
    } else if (type === 'constancia') {
      url = `${window.location.origin}/verificar-constancia/${badge.verification_code}`;
    } else {
      url = `${window.location.origin}/mis-insignias`;
    }
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  };

  const totalPoints = earnedBadges.reduce((sum, b) => sum + (b.points || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const LevelIcon = userLevel?.current?.icon ? LEVEL_ICONS[userLevel.current.icon] : Medal;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <canvas ref={canvasRef} className="hidden" />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Mis Insignias y Logros
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Descarga insignias personalizadas para compartir en redes sociales y demuestra tus certificaciones UPIM
          </p>
        </div>

        {/* Level Card */}
        {userLevel && (
          <div 
            className="rounded-2xl p-6 mb-8 text-white shadow-lg"
            style={{ 
              background: `linear-gradient(135deg, ${userLevel.current?.color || '#cd7f32'}, ${userLevel.current?.color || '#cd7f32'}90)`
            }}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
                  <LevelIcon className="w-10 h-10" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">Tu Nivel</p>
                  <h2 className="text-3xl font-bold">{userLevel.current?.name || 'Bronce'}</h2>
                  <p className="text-white/90">{totalPoints} puntos totales</p>
                </div>
              </div>
              {userLevel.next && (
                <div className="w-full md:w-64">
                  <div className="flex justify-between text-sm mb-2">
                    <span>Progreso a {userLevel.next.name}</span>
                    <span>{Math.round(userLevel.progress_percent)}%</span>
                  </div>
                  <div className="w-full bg-white/30 rounded-full h-3">
                    <div 
                      className="h-3 rounded-full bg-white transition-all duration-500"
                      style={{ width: `${userLevel.progress_percent}%` }}
                    />
                  </div>
                  <p className="text-sm text-white/80 mt-2">
                    Te faltan {userLevel.next.points_needed} puntos para subir de nivel
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
            <Gift className="w-8 h-8 mx-auto mb-2 text-indigo-500" />
            <p className="text-2xl font-bold text-gray-900">{earnedBadges.length}</p>
            <p className="text-sm text-gray-500">Insignias</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
            <Star className="w-8 h-8 mx-auto mb-2 text-amber-500" />
            <p className="text-2xl font-bold text-gray-900">{totalPoints}</p>
            <p className="text-sm text-gray-500">Puntos</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
            <Award className="w-8 h-8 mx-auto mb-2 text-purple-500" />
            <p className="text-2xl font-bold text-gray-900">{constancias.length}</p>
            <p className="text-sm text-gray-500">Constancias</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
            <Trophy className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold text-gray-900">{recognitions.length}</p>
            <p className="text-sm text-gray-500">Reconocimientos</p>
          </div>
        </div>

        {/* Portfolio Link */}
        {progress?.affiliation_number && (
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 mb-8 text-white">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-bold text-lg mb-1">Tu Portafolio Público</h3>
                <p className="text-indigo-200 text-sm">
                  Comparte este enlace para mostrar todas tus certificaciones
                </p>
              </div>
              <div className="flex items-center gap-3">
                <code className="bg-white/20 px-4 py-2 rounded-lg text-sm">
                  {window.location.origin}/portafolio/{progress.affiliation_number}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/portafolio/${progress.affiliation_number}`);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30"
                >
                  {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab('badges')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'badges'
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="flex items-center gap-2">
              <Gift className="w-4 h-4" />
              Insignias ({earnedBadges.length})
            </span>
          </button>
          <button
            onClick={() => setActiveTab('progress')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'progress'
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              Por Desbloquear ({badgeProgress.filter(b => !b.is_secret).length})
            </span>
          </button>
          <button
            onClick={() => setActiveTab('constancias')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'constancias'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="flex items-center gap-2">
              <Award className="w-4 h-4" />
              Constancias ({constancias.length})
            </span>
          </button>
          <button
            onClick={() => setActiveTab('recognitions')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === 'recognitions'
                ? 'bg-amber-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span className="flex items-center gap-2">
              <Trophy className="w-4 h-4" />
              Reconocimientos ({recognitions.length})
            </span>
          </button>
        </div>

        {/* Earned Badges */}
        {activeTab === 'badges' && (
          <div>
            {earnedBadges.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <Gift className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Aún no tienes insignias
                </h3>
                <p className="text-gray-500">
                  Completa cursos y obtén constancias para desbloquear insignias
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {earnedBadges.map(badge => (
                  <div
                    key={badge.id}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div 
                      className="p-6 text-center text-white relative"
                      style={{ background: `linear-gradient(135deg, ${badge.color || '#6366f1'}, ${badge.color || '#6366f1'}90)` }}
                    >
                      {badge.image_url ? (
                        <img 
                          src={badge.image_url} 
                          alt={badge.name}
                          className="w-24 h-24 mx-auto mb-3 object-contain drop-shadow-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
                          <Star className="w-8 h-8" />
                        </div>
                      )}
                      <h3 className="font-bold text-lg">{badge.name}</h3>
                      <p className="text-white/80 text-sm mt-1">{badge.description}</p>
                      <p className="mt-2 font-medium">+{badge.points} puntos</p>
                    </div>
                    
                    <div className="p-4">
                      <p className="text-xs text-gray-500 text-center mb-3">
                        Obtenida el {new Date(badge.earned_at).toLocaleDateString('es-MX')}
                      </p>
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleDownloadBadge(badge, 'badge')}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg"
                          style={{ backgroundColor: `${badge.color || '#6366f1'}15`, color: badge.color || '#6366f1' }}
                        >
                          <Download className="w-3 h-3" />
                          Descargar
                        </button>
                        <button
                          onClick={() => handleShareTwitter(badge, 'badge')}
                          className="p-1.5 text-blue-400 hover:bg-blue-50 rounded-lg"
                        >
                          <Twitter className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleShareLinkedIn(badge, 'badge')}
                          className="p-1.5 text-blue-700 hover:bg-blue-50 rounded-lg"
                        >
                          <Linkedin className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleShareFacebook(badge, 'badge')}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Facebook className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Badge Progress */}
        {activeTab === 'progress' && (
          <div>
            {badgeProgress.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <CheckCircle className="w-16 h-16 mx-auto text-green-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  ¡Has desbloqueado todas las insignias!
                </h3>
                <p className="text-gray-500">
                  Felicidades, eres un experto UPIM
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {badgeProgress.filter(b => !b.is_secret).map(badge => (
                  <div
                    key={badge.badge_id}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-stretch">
                      <div 
                        className="w-32 flex-shrink-0 flex items-center justify-center p-4"
                        style={{ backgroundColor: `${badge.color || '#6366f1'}15` }}
                      >
                        {badge.image_url ? (
                          <img 
                            src={badge.image_url} 
                            alt={badge.name}
                            className="w-20 h-20 object-contain opacity-70"
                          />
                        ) : (
                          <Lock className="w-10 h-10 opacity-50" style={{ color: badge.color || '#6366f1' }} />
                        )}
                      </div>
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-gray-900">{badge.name}</h3>
                            <p className="text-sm text-gray-500">{badge.description}</p>
                          </div>
                          <div className="text-right ml-2">
                            <p className="text-lg font-bold" style={{ color: badge.color || '#6366f1' }}>
                              +{badge.points}
                            </p>
                            <p className="text-xs text-gray-500">puntos</p>
                          </div>
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{badge.current_progress} / {badge.criteria_value} constancias</span>
                            <span>{Math.round(badge.progress_percent)}%</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2.5">
                            <div
                              className="h-2.5 rounded-full transition-all duration-500"
                              style={{ 
                                width: `${badge.progress_percent}%`,
                                backgroundColor: badge.color || '#6366f1'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Constancias */}
        {activeTab === 'constancias' && (
          <div>
            {constancias.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Aún no tienes constancias
                </h3>
                <p className="text-gray-500">
                  Completa cursos y webinars para obtener constancias
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {constancias.map(constancia => (
                  <div
                    key={constancia.id}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-center text-white">
                      <Award className="w-8 h-8 mx-auto mb-1" />
                      <p className="font-medium text-sm truncate">{constancia.content_title}</p>
                      <p className="text-indigo-200 text-xs capitalize">{constancia.content_type}</p>
                    </div>
                    
                    <div className="p-3">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleDownloadBadge(constancia, 'constancia')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
                        >
                          <Download className="w-3 h-3" />
                          Descargar
                        </button>
                        <button
                          onClick={() => handleShareTwitter(constancia, 'constancia')}
                          className="p-1.5 text-blue-400 hover:bg-blue-50 rounded-lg"
                        >
                          <Twitter className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleShareLinkedIn(constancia, 'constancia')}
                          className="p-1.5 text-blue-700 hover:bg-blue-50 rounded-lg"
                        >
                          <Linkedin className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recognitions */}
        {activeTab === 'recognitions' && (
          <div>
            {recognitions.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl">
                <Trophy className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Aún no tienes reconocimientos
                </h3>
                <p className="text-gray-500">
                  Acumula constancias para obtener reconocimientos especiales
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {recognitions.map(recognition => (
                  <div
                    key={recognition.id}
                    className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200 rounded-xl overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-amber-500 to-yellow-500 p-6 text-center text-white">
                      <Trophy className="w-12 h-12 mx-auto mb-2" />
                      <h3 className="font-bold text-lg">{recognition.program_name}</h3>
                      <p className="text-amber-100 text-sm">{recognition.total_constancias} constancias</p>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2 justify-center">
                        <button
                          onClick={() => handleDownloadBadge(recognition, 'recognition')}
                          className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                        >
                          <Download className="w-4 h-4" />
                          Descargar
                        </button>
                        <button
                          onClick={() => handleCopyLink(recognition, 'recognition')}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                        >
                          <Link2 className="w-4 h-4" />
                          Copiar enlace
                        </button>
                      </div>
                      <div className="flex justify-center gap-2 mt-3">
                        <button
                          onClick={() => handleShareTwitter(recognition, 'recognition')}
                          className="p-2 text-blue-400 hover:bg-blue-50 rounded-lg"
                        >
                          <Twitter className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleShareLinkedIn(recognition, 'recognition')}
                          className="p-2 text-blue-700 hover:bg-blue-50 rounded-lg"
                        >
                          <Linkedin className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleShareFacebook(recognition, 'recognition')}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Facebook className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
